/**
 * Mock data for DriverGauge
 * Provides realistic sample data for development and testing
 */

import type {
  Driver,
  Note,
  GaugeItem,
  NoteCategory,
  NotePriority,
  GaugeFrequency,
} from '../types/driver';

/* ===========================================
   MOCK DRIVERS
   =========================================== */

export const mockDrivers: Driver[] = [
  {
    id: 'd1',
    employeeId: 'DRV-001',
    firstName: 'Marcus',
    lastName: 'Johnson',
    displayName: 'Marcus Johnson',
    photoUrl: null,
    email: 'marcus.johnson@example.com',
    phone: '(555) 123-4567',
    status: 'active',
    tier: 'gold',
    stats: {
      totalDeliveries: 2847,
      monthlyDeliveries: 156,
      onTimeRate: 98.2,
      customerRating: 4.8,
      safetyScore: 95,
      daysSinceIncident: 342,
    },
    badges: [
      {
        id: 'b1',
        name: 'Safety Champion',
        icon: 'shield',
        description: '1 year without incidents',
        earnedAt: '2025-06-15T00:00:00Z',
        category: 'safety',
      },
      {
        id: 'b2',
        name: '5-Star Driver',
        icon: 'star',
        description: '100+ 5-star reviews',
        earnedAt: '2025-08-20T00:00:00Z',
        category: 'performance',
      },
    ],
    hiredAt: '2022-03-01T00:00:00Z',
    updatedAt: '2026-01-03T14:30:00Z',
  },
  {
    id: 'd2',
    employeeId: 'DRV-002',
    firstName: 'Sarah',
    lastName: 'Chen',
    displayName: 'Sarah Chen',
    photoUrl: null,
    email: 'sarah.chen@example.com',
    phone: '(555) 234-5678',
    status: 'active',
    tier: 'platinum',
    stats: {
      totalDeliveries: 4521,
      monthlyDeliveries: 198,
      onTimeRate: 99.1,
      customerRating: 4.9,
      safetyScore: 98,
      daysSinceIncident: 567,
    },
    badges: [
      {
        id: 'b3',
        name: 'Top Performer',
        icon: 'trophy',
        description: 'Highest monthly deliveries',
        earnedAt: '2025-12-01T00:00:00Z',
        category: 'performance',
      },
      {
        id: 'b4',
        name: '3-Year Veteran',
        icon: 'award',
        description: '3+ years of service',
        earnedAt: '2025-09-15T00:00:00Z',
        category: 'tenure',
      },
    ],
    hiredAt: '2022-09-15T00:00:00Z',
    updatedAt: '2026-01-04T09:15:00Z',
  },
  {
    id: 'd3',
    employeeId: 'DRV-003',
    firstName: 'James',
    lastName: 'Wilson',
    displayName: 'James Wilson',
    photoUrl: null,
    email: 'james.wilson@example.com',
    phone: '(555) 345-6789',
    status: 'on_break',
    tier: 'silver',
    stats: {
      totalDeliveries: 1234,
      monthlyDeliveries: 89,
      onTimeRate: 94.5,
      customerRating: 4.5,
      safetyScore: 88,
      daysSinceIncident: 45,
    },
    badges: [
      {
        id: 'b5',
        name: 'Quick Learner',
        icon: 'zap',
        description: 'Completed training ahead of schedule',
        earnedAt: '2024-11-10T00:00:00Z',
        category: 'training',
      },
    ],
    hiredAt: '2024-06-01T00:00:00Z',
    updatedAt: '2026-01-04T08:00:00Z',
  },
];

/* ===========================================
   MOCK NOTES
   =========================================== */

export const mockNotes: Note[] = [
  {
    id: 'n1',
    driverId: 'd1',
    content:
      'Excellent performance this quarter. Marcus has consistently exceeded delivery targets and received multiple customer commendations.',
    category: 'commendation',
    priority: 'low',
    tags: ['quarterly-review', 'performance'],
    createdBy: 'u1',
    createdByName: 'Manager Smith',
    createdAt: '2026-01-02T10:30:00Z',
    updatedAt: '2026-01-02T10:30:00Z',
    isPinned: true,
    isFollowUp: false,
    followUpDate: null,
  },
  {
    id: 'n2',
    driverId: 'd1',
    content:
      'Completed advanced defensive driving course. Certificate added to personnel file.',
    category: 'training',
    priority: 'medium',
    tags: ['training', 'certification'],
    createdBy: 'u2',
    createdByName: 'Training Coordinator',
    createdAt: '2025-12-15T14:00:00Z',
    updatedAt: '2025-12-15T14:00:00Z',
    isPinned: false,
    isFollowUp: false,
    followUpDate: null,
  },
  {
    id: 'n3',
    driverId: 'd1',
    content:
      'Minor fender bender in parking lot. No injuries, minimal damage. Insurance claim filed. Follow up required on vehicle repair status.',
    category: 'safety',
    priority: 'high',
    tags: ['incident', 'vehicle-damage', 'insurance'],
    createdBy: 'u1',
    createdByName: 'Manager Smith',
    createdAt: '2025-11-20T16:45:00Z',
    updatedAt: '2025-11-21T09:00:00Z',
    isPinned: false,
    isFollowUp: true,
    followUpDate: '2026-01-15T00:00:00Z',
  },
  {
    id: 'n4',
    driverId: 'd2',
    content:
      'Sarah achieved platinum tier this month. Schedule recognition at next team meeting.',
    category: 'commendation',
    priority: 'medium',
    tags: ['promotion', 'recognition'],
    createdBy: 'u1',
    createdByName: 'Manager Smith',
    createdAt: '2026-01-01T11:00:00Z',
    updatedAt: '2026-01-01T11:00:00Z',
    isPinned: true,
    isFollowUp: true,
    followUpDate: '2026-01-10T00:00:00Z',
  },
  {
    id: 'n5',
    driverId: 'd3',
    content:
      'Customer complaint regarding late delivery on 12/28. Issue was weather-related, not driver fault. Explained to customer, no further action needed.',
    category: 'customer',
    priority: 'medium',
    tags: ['complaint', 'resolved'],
    createdBy: 'u3',
    createdByName: 'Customer Service',
    createdAt: '2025-12-29T10:15:00Z',
    updatedAt: '2025-12-30T14:30:00Z',
    isPinned: false,
    isFollowUp: false,
    followUpDate: null,
  },
];

/* ===========================================
   MOCK GAUGE ITEMS
   =========================================== */

export const mockGaugeItems: GaugeItem[] = [
  // Daily items
  {
    id: 'p1',
    driverId: 'd1',
    title: 'Vehicle inspection completed',
    description: 'Pre-trip vehicle safety inspection',
    frequency: 'daily',
    status: 'completed',
    createdAt: '2025-11-15T00:00:00Z',
    createdBy: 'Manager Smith',
    lastCompletedAt: '2026-01-04T06:30:00Z',
    lastCompletedBy: 'Manager Smith',
    dueDate: '2026-01-04T00:00:00Z',
    isRequired: true,
    order: 1,
  },
  {
    id: 'p2',
    driverId: 'd1',
    title: 'Hours of service logged',
    description: 'Verify HOS compliance',
    frequency: 'daily',
    status: 'completed',
    createdAt: '2025-11-15T00:00:00Z',
    createdBy: 'Manager Smith',
    lastCompletedAt: '2026-01-04T06:35:00Z',
    lastCompletedBy: 'Manager Smith',
    dueDate: '2026-01-04T00:00:00Z',
    isRequired: true,
    order: 2,
  },
  {
    id: 'p3',
    driverId: 'd1',
    title: 'Route briefing acknowledged',
    description: 'Driver reviewed daily route assignments',
    frequency: 'daily',
    status: 'pending',
    createdAt: '2025-11-15T00:00:00Z',
    createdBy: 'Manager Smith',
    lastCompletedAt: '2026-01-03T07:00:00Z',
    lastCompletedBy: 'Manager Smith',
    dueDate: '2026-01-04T00:00:00Z',
    isRequired: true,
    order: 3,
  },

  // Weekly items
  {
    id: 'p4',
    driverId: 'd1',
    title: 'Weekly performance review',
    description: 'Review delivery metrics and customer feedback',
    frequency: 'weekly',
    status: 'pending',
    createdAt: '2025-12-01T00:00:00Z',
    createdBy: 'Manager Smith',
    lastCompletedAt: '2025-12-28T10:00:00Z',
    lastCompletedBy: 'Manager Smith',
    dueDate: '2026-01-06T00:00:00Z',
    isRequired: true,
    order: 1,
  },
  {
    id: 'p5',
    driverId: 'd1',
    title: 'Safety check-in',
    description: 'Weekly safety discussion and concerns',
    frequency: 'weekly',
    status: 'pending',
    createdAt: '2025-12-01T00:00:00Z',
    createdBy: 'Safety Officer',
    lastCompletedAt: '2025-12-27T14:00:00Z',
    lastCompletedBy: 'Safety Officer',
    dueDate: '2026-01-06T00:00:00Z',
    isRequired: true,
    order: 2,
  },

  // Monthly items
  {
    id: 'p6',
    driverId: 'd1',
    title: 'License verification',
    description: 'Verify driver license is valid and current',
    frequency: 'monthly',
    status: 'completed',
    createdAt: '2025-10-01T00:00:00Z',
    createdBy: 'HR Department',
    lastCompletedAt: '2026-01-02T09:00:00Z',
    lastCompletedBy: 'HR Department',
    dueDate: '2026-02-01T00:00:00Z',
    isRequired: true,
    order: 1,
  },
  {
    id: 'p7',
    driverId: 'd1',
    title: 'Equipment inventory',
    description: 'Review and update assigned equipment list',
    frequency: 'monthly',
    status: 'pending',
    createdAt: '2025-10-01T00:00:00Z',
    createdBy: 'Equipment Manager',
    lastCompletedAt: '2025-12-05T11:00:00Z',
    lastCompletedBy: 'Equipment Manager',
    dueDate: '2026-01-05T00:00:00Z',
    isRequired: false,
    order: 2,
  },

  // As needed items
  {
    id: 'p8',
    driverId: 'd1',
    title: 'Incident report filed',
    description: 'Complete if any incidents occurred',
    frequency: 'as_needed',
    status: 'pending',
    createdAt: '2025-09-01T00:00:00Z',
    createdBy: 'Safety Officer',
    lastCompletedAt: null,
    lastCompletedBy: null,
    dueDate: null,
    isRequired: false,
    order: 1,
  },
  {
    id: 'p9',
    driverId: 'd1',
    title: 'Customer commendation logged',
    description: 'Record positive customer feedback',
    frequency: 'as_needed',
    status: 'pending',
    createdAt: '2025-09-01T00:00:00Z',
    createdBy: 'Manager Smith',
    lastCompletedAt: '2025-12-20T15:30:00Z',
    lastCompletedBy: 'Manager Smith',
    dueDate: null,
    isRequired: false,
    order: 2,
  },
];

/* ===========================================
   HELPER FUNCTIONS
   =========================================== */

/**
 * Get a driver by ID
 */
export function getDriverById(id: string): Driver | undefined {
  return mockDrivers.find((driver) => driver.id === id);
}

/**
 * Get notes for a specific driver
 */
export function getNotesForDriver(driverId: string): Note[] {
  return mockNotes
    .filter((note) => note.driverId === driverId)
    .sort((a, b) => {
      // Pinned notes first
      if (a.isPinned && !b.isPinned) {
        return -1;
      }
      if (!a.isPinned && b.isPinned) {
        return 1;
      }
      // Then by date, newest first
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
}

/**
 * Get GAUGE ITEMS for a specific driver
 */
export function getGaugeItemsForDriver(driverId: string): GaugeItem[] {
  return mockGaugeItems
    .filter((item) => item.driverId === driverId)
    .sort((a, b) => a.order - b.order);
}

/**
 * Get currently selected driver (for demo purposes)
 */
export function getCurrentDriver(): Driver {
  return mockDrivers[0];
}

/**
 * Get note category label
 */
export function getNoteCategoryLabel(category: NoteCategory): string {
  const labels: Record<NoteCategory, string> = {
    general: 'General',
    performance: 'Performance',
    safety: 'Safety',
    customer: 'Customer',
    equipment: 'Equipment',
    training: 'Training',
    disciplinary: 'Disciplinary',
    commendation: 'Commendation',
  };
  return labels[category];
}

/**
 * Get note category color class
 */
export function getNoteCategoryColor(category: NoteCategory): string {
  const colors: Record<NoteCategory, string> = {
    general: 'category-general',
    performance: 'category-performance',
    safety: 'category-safety',
    customer: 'category-customer',
    equipment: 'category-equipment',
    training: 'category-training',
    disciplinary: 'category-disciplinary',
    commendation: 'category-commendation',
  };
  return colors[category];
}

/**
 * Get note category tab color (hex value for color tab design)
 */
export function getCategoryTabColor(category: NoteCategory): string {
  const colors: Record<NoteCategory, string> = {
    safety: '#ef4444',
    performance: '#22c55e',
    customer: '#a855f7',
    equipment: '#f59e0b',
    training: '#3b82f6',
    general: '#64748b',
    disciplinary: '#dc2626',
    commendation: '#10b981',
  };
  return colors[category];
}

/**
 * Get priority label
 */
export function getPriorityLabel(priority: NotePriority): string {
  const labels: Record<NotePriority, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
  };
  return labels[priority];
}

/**
 * Get frequency label
 */
export function getFrequencyLabel(frequency: GaugeFrequency): string {
  const labels: Record<GaugeFrequency, string> = {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    as_needed: 'As Needed',
  };
  return labels[frequency];
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  if (diffDays < 30) {
    return `${Math.floor(diffDays / 7)}w ago`;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
